import PropTypes from 'prop-types';
import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import Icon from 'components/commons/Icon';

import { TablePlaceholder } from 'components/ContentPlaceholder';

import Tooltip from 'components/Tooltip';
import { TABLE_WATERMARK_POSITIONS } from 'constants/tableWatermark';
import {
  Wrapper,
  PlainWrapper,
  TableWrapper,
  TableHeader,
  Title,
  Row,
  Currency,
  Curr,
  CurrName,
  CurrContent,
  DiffWrapper,
  Diff,
  Button,
  Code,
  CurrencyLink,
} from './styles';

const showEvents = ['mouseenter', 'touchend'];
const hideEvents = ['mouseleave'];

export const Table = ({
  titles,
  rows,
  type,
  anotherCurrTable,
  activeCurrency,
  getHref,
  onClick,
  className,
  placeholder,
  buttonHref,
  withoutWatermark,
  watermarkLeft,
  watermarkLeftMobile,
}) => {
  const intl = useIntl();
  const { locale } = useIntl();
  const watermarkPosition =
    TABLE_WATERMARK_POSITIONS[type] || TABLE_WATERMARK_POSITIONS.default;

  const renderCurrency = (price, diff, anotherCurrTable, type) => {
    const isDiffEmpty = diff === '-';
    const convertedDiff = isDiffEmpty ? 0 : Number(diff);

    return (
      <Curr anotherCurrTable={anotherCurrTable}>
        <CurrContent type={type}>
          {price &&
            (type === 'mb' && !Number.isNaN(Number(price))
              ? Number(price).toFixed(4)
              : price.toString()
            ).replace('.', ',')}
          {!isDiffEmpty && convertedDiff !== 0 ? (
            <DiffWrapper data-tip-target fall={convertedDiff < 0}>
              <Icon name={convertedDiff < 0 ? 'polygon' : 'growth'} />
              <Diff>{convertedDiff.toFixed(2)}</Diff>

              {type === 'mb' && (
                <Tooltip
                  show
                  position="top"
                  width={138}
                  showEvents={showEvents}
                  hideEvents={hideEvents}
                >
                  {intl.formatMessage({ id: 'mbRatesTooltip' })}
                </Tooltip>
              )}
            </DiffWrapper>
          ) : (
            <DiffWrapper>
              <Diff>{isDiffEmpty ? '-' : '0.00'}</Diff>
            </DiffWrapper>
          )}
        </CurrContent>
      </Curr>
    );
  };
  const Container = withoutWatermark ? PlainWrapper : Wrapper;

  return (
    <Container
      className={className}
      {...(withoutWatermark
        ? {}
        : {
            watermarkLeft: watermarkLeft || watermarkPosition.left,
            watermarkLeftMobile:
              watermarkLeftMobile || watermarkPosition.leftMobile,
          })}
    >
      <TableWrapper>
        <TableHeader>
          <Row>
            {titles.map(el => (
              <Title key={el} type={type}>
                <FormattedMessage id={`table.${el}.title`} />
              </Title>
            ))}
          </Row>
        </TableHeader>

        <tbody>
          {placeholder && rows && rows.length === 0 && (
            <TablePlaceholder type={type} />
          )}

          {rows &&
            rows.map(el => {
              if (!el) {
                return null;
              }

              const currencyKey = el.currency || el.code;
              const currencySlug = currencyKey
                ? String(currencyKey).toLowerCase()
                : '';

              return (
                <Row key={currencyKey || 'row'}>
                  {type === 'nbu' && (
                    <Code anotherCurrTable={anotherCurrTable}>{el.iso}</Code>
                  )}

                  <Currency href="#" type={type}>
                    {getHref ? (
                      <CurrencyLink
                        onClick={onClick ? () => onClick(currencyKey) : null}
                        anotherCurrTable={anotherCurrTable}
                        href={getHref(currencySlug, locale)}
                        to={getHref(currencySlug, locale)}
                        isActive={currencySlug === activeCurrency}
                      >
                        {el.currency?.toUpperCase() || el.code}
                      </CurrencyLink>
                    ) : (
                      el.currency?.toUpperCase()
                    )}
                  </Currency>

                  {type === 'nbu' ? (
                    <CurrName anotherCurrTable={anotherCurrTable}>
                      {el.name}
                    </CurrName>
                  ) : (
                    renderCurrency(el.bid, el.trendBid, anotherCurrTable, type)
                  )}

                  {type !== 'nbu' &&
                    renderCurrency(el.ask, el.trendAsk, anotherCurrTable, type)}

                  {type === 'nbu' &&
                    renderCurrency(el.rate, el.trend, anotherCurrTable, type)}

                  {type === 'average' && renderCurrency(el.nbu, el.diffNBU)}

                  {buttonHref && (
                    <td style={{ textAlign: 'right' }}>
                      <Button
                        as="a"
                        target="_blank"
                        href={buttonHref(currencySlug)}
                      >
                        <FormattedMessage id="table.exchange.button" />
                      </Button>
                    </td>
                  )}
                </Row>
              );
            })}
        </tbody>
      </TableWrapper>
    </Container>
  );
};

Table.propTypes = {
  type: PropTypes.string.isRequired,
  titles: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      cur: PropTypes.string,
      buy: PropTypes.string,
      sell: PropTypes.string,
      nbu: PropTypes.string,
      diffBuy: PropTypes.string,
      diffSell: PropTypes.string,
      diffNBU: PropTypes.string,
      button: PropTypes.bool,
    })
  ).isRequired,
  anotherCurrTable: PropTypes.bool,
  activeCurrency: PropTypes.string,
  getHref: PropTypes.func,
  onClick: PropTypes.func,
  className: PropTypes.string,
  placeholder: PropTypes.bool,
  buttonHref: PropTypes.func,
  withoutWatermark: PropTypes.bool,
  watermarkLeft: PropTypes.string,
  watermarkLeftMobile: PropTypes.string,
};

Table.defaultProps = {
  anotherCurrTable: null,
  activeCurrency: null,
  getHref: null,
  onClick: null,
  className: null,
  placeholder: true,
  buttonHref: null,
  withoutWatermark: false,
  watermarkLeft: null,
  watermarkLeftMobile: null,
};
